import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// withCredentials so the Emergent-Google session_token cookie is sent for protected endpoints
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('apex_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // CSRF hardening — every state-changing request carries a custom
  // header that a cross-origin browser cannot forge. Cookie-based
  // sessions require this header (server enforces on POST/PUT/
  // PATCH/DELETE when session_token is present).
  const method = (config.method || 'get').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    config.headers['X-Apex-Request'] = '1';
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Gateway 429 on read-only bursts (dashboard fan-out): retry GETs up to
    // 2× with jittered backoff before surfacing the error.
    const cfg = err?.config || {};
    const isGet = (cfg.method || 'get').toLowerCase() === 'get';
    if (err?.response?.status === 429 && isGet && (cfg.__retry || 0) < 2) {
      cfg.__retry = (cfg.__retry || 0) + 1;
      const wait = 400 * cfg.__retry + Math.random() * 400;
      await new Promise((res) => setTimeout(res, wait));
      return api.request(cfg);
    }
    if (err?.response?.status === 401) {
      // /auth/refresh handles its own 401s (max-age message in SessionExpiryGuard)
      if (err?.config?.url?.includes('/auth/refresh')) return Promise.reject(err);
      localStorage.removeItem('apex_token');
      localStorage.removeItem('apex_user');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const auth = {
  login: async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const { data } = await api.post('/auth/login', payload);
    if (data.access_token) {
      localStorage.setItem('apex_token', data.access_token);
      localStorage.setItem('apex_user', JSON.stringify(data.user));
    }
    return data;
  },
  register: async (email, password, role = 'operator') => {
    const { data } = await api.post('/auth/register', { email, password, role });
    localStorage.setItem('apex_token', data.access_token);
    localStorage.setItem('apex_user', JSON.stringify(data.user));
    return data;
  },
  logout: async () => {
    try { await api.post('/auth/logout'); } catch (e) { /* ignore */ }
    localStorage.removeItem('apex_token');
    localStorage.removeItem('apex_user');
  },
  currentUser: () => {
    const raw = localStorage.getItem('apex_user');
    return raw ? JSON.parse(raw) : null;
  },
  // Authed if we have either a JWT token OR a stored user (cookie session path)
  isAuthed: () => Boolean(localStorage.getItem('apex_token') || localStorage.getItem('apex_user')),
};
